import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  getProfile,
  isInvalidEmailError,
  updateProfile,
} from '../api/rttsClient';
import { STUDIO_COPY, STUDIO_DOCUMENT_TITLES } from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { notifyStudioOnboardingCompleted } from '../hooks/studioOnboardingSession';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * One-time onboarding — welcome + how-it-works + optional email.
 */
const StudioOnboardingPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.onboarding);
  const navigate = useNavigate();
  const { status, isLoggedOut } = useStudioAuth();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoggedOut) {
      setChecking(false);
      return;
    }
    if (status === 'loading') return;
    if (status !== 'linked') {
      setChecking(false);
      return;
    }
    let cancelled = false;
    void getProfile()
      .then((profile) => {
        if (cancelled) return;
        if (profile.onboarded) {
          navigate('/studio/vods', { replace: true });
          return;
        }
        if (profile.email) setEmail(profile.email);
        setChecking(false);
      })
      .catch(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, isLoggedOut, navigate]);

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'loading' || checking) {
    return (
      <div className="flex justify-center py-16" data-testid="studio-onboarding-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  const handleContinue = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setEmailError(null);
    setSubmitError(null);

    const trimmed = email.trim();
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setEmailError(STUDIO_COPY.invalidEmail);
      return;
    }

    setSubmitting(true);
    try {
      const patch: { onboarded: true; email?: string } = { onboarded: true };
      if (trimmed) patch.email = trimmed;
      await updateProfile(patch);
      notifyStudioOnboardingCompleted();
      navigate('/studio/vods', { replace: true });
    } catch (error) {
      if (isInvalidEmailError(error)) {
        setEmailError(STUDIO_COPY.invalidEmail);
        setSubmitting(false);
        return;
      }
      setSubmitError(STUDIO_COPY.onboardingSaveFailed);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="studio-page studio-fade-up"
      data-testid="studio-onboarding"
    >
      <div className="studio-section">
        <h1 className="studio-display text-3xl">{STUDIO_COPY.onboardingWelcome}</h1>
        <p className="studio-page-kicker">
          {STUDIO_COPY.onboardingSubhead}
        </p>
      </div>

      <ol
        className="studio-panel space-y-3"
        data-testid="studio-onboarding-steps"
      >
        <li className="flex gap-3">
          <span className="text-[var(--studio-accent-bright)] font-semibold">1</span>
          <span>{STUDIO_COPY.onboardingStepConnect}</span>
        </li>
        <li className="flex gap-3">
          <span className="text-[var(--studio-accent-bright)] font-semibold">2</span>
          <span>{STUDIO_COPY.onboardingStepPick}</span>
        </li>
        <li className="flex gap-3">
          <span className="text-[var(--studio-accent-bright)] font-semibold">3</span>
          <span>{STUDIO_COPY.onboardingStepWait}</span>
        </li>
      </ol>

      <form className="studio-panel space-y-4" onSubmit={(e) => void handleContinue(e)}>
        <div className="space-y-2">
          <label htmlFor="studio-onboarding-email" className="text-sm text-[var(--studio-muted)]">
            {STUDIO_COPY.onboardingEmailLabel}{' '}
            <span className="opacity-70">{STUDIO_COPY.onboardingEmailOptional}</span>
          </label>
          <input
            id="studio-onboarding-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            className="studio-input w-full"
            placeholder={STUDIO_COPY.onboardingEmailPlaceholder}
            data-testid="studio-onboarding-email"
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'studio-onboarding-email-error' : undefined}
          />
          {emailError ? (
            <p
              id="studio-onboarding-email-error"
              className="text-sm text-[var(--studio-danger)]"
              role="alert"
              data-testid="studio-onboarding-email-error"
            >
              {emailError}
            </p>
          ) : null}
        </div>

        {submitError ? (
          <p className="text-sm text-[var(--studio-danger)]" role="alert">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          className="studio-btn-primary w-full"
          disabled={submitting}
          aria-busy={submitting}
          data-testid="studio-onboarding-continue"
        >
          {submitting ? STUDIO_COPY.onboardingSaving : STUDIO_COPY.onboardingContinue}
        </button>
      </form>
    </div>
  );
};

export default StudioOnboardingPage;
